import { config } from "@/app/config";
import { AuthUrls, QrSessionState } from "@/modules/auth/common/enum";
import { useGetQrQuery, useQrExchangeMutation } from "@/modules/auth/services";
import { setIsAuth } from "@/modules/auth/state";
import { AppPaths, CloudflareAsset, SnackbarAlertTypes, Storage } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, EmptyOrErrorContent } from "@/shared/components";
import { getEventStreamHeaders, setToLocalStorage, setUserToStorage } from "@/shared/helpers";
import { useAppDispatch, useCountDownTimer, useNotify } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import classNames from "classnames";
import { useDeferredValue, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCode } from "react-qrcode-logo";
import { useNavigate } from "react-router-dom";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const QrModal = ({ onClose, isOpen }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showErrorSnackbar, showSnackbar } = useNotify();
  const controller = new AbortController();

  const {
    data,
    error,
    refetch,
    isFetching,
  } = useGetQrQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
      skip: !isOpen,
    }
  );

  const {
    timer,
    restartTimer
  } = useCountDownTimer({
    duration: data?.expiresInSeconds || 0,
    isStartOnMount: false,
  });
  const [qrExchange] = useQrExchangeMutation();

  const qrCode = useDeferredValue(data?.qrText);
  const [status, setStatus] = useState<QrSessionState>(QrSessionState.None);

  const handleCloseAndAbort = () => {
    controller.abort();
    onClose();
  };

  const getQRStatus = () => {
    const headers = getEventStreamHeaders();
    void fetchEventSource(`${config.appBaseUrl}${AuthUrls.QRStatus}`, {
      method: "GET",
      headers,
      openWhenHidden: true,
      credentials: "include",
      signal: controller.signal,
      onmessage(res) {
        const parsed = JSON.parse(res.data);
        setStatus(parsed.state);
        if (parsed.state === QrSessionState.Canceled) {
          handleCloseAndAbort();
          showSnackbar({
            message: t("auth.login.qrModal.canceled"),
            type: SnackbarAlertTypes.Info,
          });
        } else if (parsed.state === QrSessionState.Approved) {
          qrExchange(data!.csrfToken)
            .unwrap()
            .then((res) => {
              const user = res?.user;
              if (user) {
                setUserToStorage(user);
                setToLocalStorage(Storage.IsAuth, "true");
                dispatch(setIsAuth(true));
                navigate(AppPaths.Dashboard);
              }
            });
          handleCloseAndAbort();
        } else if (parsed.state === QrSessionState.Expired) {
          controller.abort();
        } else if (parsed.state === QrSessionState.Failed) {
          showErrorSnackbar();
          handleCloseAndAbort();
        }
      },
      onerror(err) {
        controller.abort();
        showErrorSnackbar(err);
        onClose();
      }
    });
  };

  useEffect(() => {
    if (!isFetching && data?.expiresInSeconds) {
      restartTimer();
      getQRStatus();
    }
  }, [isFetching, data]);

  useEffect(() => {
    if (error) {
      showErrorSnackbar(error as IError);
    }
  }, [error]);

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);

  const isExpired = status === QrSessionState.Expired;
  const isScanned = status === QrSessionState.Scanned;
  const isFailed = status === QrSessionState.Failed;

  return (
    <Modal
      closeIcon
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => {}}
      className="qr-login-modal"
      closeOnOutsideClick={false}
      titleProps={{
        title: t("auth.login.qrModal.title")
      }}
    >
      <div>
        <Text>{t("auth.login.qrModal.subtitle")}</Text>
        <div className="mt-16 qr-wrapper">
          <div className="w-fit-content qr-body-wrapper">
            {(isFetching || isScanned) && (
              <Spinner
                isFullScreen={false}
                size="small"
                className="full-width full-height"
              />
            )}
            {!isFetching && !isFailed && (
              <div className={classNames({ "qr-blur": isExpired || isScanned })}>
                <QRCode
                  value={qrCode}
                  qrStyle="dots"
                  eyeRadius={{
                    outer: 10,
                    inner: 0,
                  }}
                  quietZone={8}
                  size={188}
                />
              </div>
            )}
            {isFailed && (
              <EmptyOrErrorContent
                title={t("auth.login.qrModal.failed.title")}
                subtitle={t("auth.login.qrModal.failed.subtitle")}
                illustration={CloudflareAsset.Error}
                action={refetch}
              />
            )}
          </div>
          {!isFailed && (
            <div
              className="countdown-wrapper mt-16">
              {!isExpired ? (
                <Text className="timer">{t("auth.login.qrModal.expiresIn")} {timer}</Text>
              ) : (
                <Button
                  type="secondary"
                  data-testid="refresh-qr"
                  data-ga-id="refresh-qr"
                  data-ga-action="Refresh QR code"
                  onClick={refetch}
                  disabled={!isExpired}
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.Refresh}/>
                    ),
                    alignment: "left",
                  }}
                >
                  {t("general.reload")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
